import { X, Save } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface EditFieldModalProps {
  isOpen: boolean;
  onClose: () => void;
  field: {
    name: string;
    value: string | null;
    status: string;
    nameKey?: string;
  };
  onSave?: (value: string) => void | Promise<void>;
}

export function EditFieldModal({
  isOpen,
  onClose,
  field,
  onSave,
}: EditFieldModalProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState(field.value || '');
  const [saving, setSaving] = useState(false);

  const fieldLabel = field.nameKey
    ? t(`epassport.${field.nameKey}`)
    : field.name;

  useEffect(() => {
    if (isOpen) setValue(field.value || '');
  }, [isOpen, field.value, field.name]);

  if (!isOpen) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      if (onSave) {
        await Promise.resolve(onSave(value));
      } else {
        toast.success(t('editFieldModal.updatedSuccess', { field: fieldLabel }));
      }
      onClose();
    } catch {
      toast.error(t('editFieldModal.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const placeholder =
    field.name === 'Weight'
      ? t('editFieldModal.weightPlaceholder')
      : t('editFieldModal.enterField', { field: fieldLabel });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl max-w-lg w-full border dark:border-white/8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b dark:border-white/8">
          <div>
            <h2 className="text-xl font-semibold dark:text-white">
              {field.status === 'missing'
                ? t('editFieldModal.add')
                : t('editFieldModal.edit')}{' '}
              {fieldLabel}
            </h2>
            <p className="text-sm text-gray-600 dark:text-white/70 mt-1">
              {t('editFieldModal.updateInfoBelow')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-white/5 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 dark:text-white" />
          </button>
        </div>

        {/* Form */}
        <div className="p-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-white/90 mb-2">
            {fieldLabel}
          </label>
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={placeholder}
            autoFocus
          />
          {field.name === 'Weight' && (
            <p className="mt-2 text-xs text-gray-500 dark:text-white/60">
              {t('editFieldModal.weightHint')}
            </p>
          )}

          {/* Info */}
          <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/30 rounded-lg p-3">
            <p className="text-xs text-blue-800 dark:text-blue-300">
              {t('editFieldModal.blockchainNote')}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 p-6 border-t dark:border-white/8 bg-gray-50 dark:bg-[#0F0E16]">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-white/20 hover:bg-gray-100 dark:hover:bg-white/5 rounded-lg text-sm dark:text-white"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={!value.trim() || saving}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg flex items-center gap-2 text-sm"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t('editFieldModal.saving')}
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {t('editFieldModal.saveChanges')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
